import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EndPoints from '../../config/EndPoint';
import { refreshSession } from '../../hooks/useFetch';
import EmailNotificationToast from './EmailNotificationToast';
import { ServiceEmailNotification } from '../../models/ServiceEmailNotificationModel';
import './styles/EmailNotificationBell.css';

const POLL_MS = 60_000;

async function fetchWithSession(url: string, init: RequestInit): Promise<Response> {
  const request = { ...init, credentials: 'include' as const };
  let response = await fetch(url, request);

  if (response.status === 401 && await refreshSession()) {
    response = await fetch(url, request);
  }

  return response;
}

function formatCreatedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toServiceId(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/**
 * Campana de notificaciones (servicios sin conductor + solicitudes).
 *
 * Contrato de lectura:
 * - El toast solo avisa; cerrarlo, expirar o abrir el ítem NO marca leída.
 * - Solo marcan leída: "Marcar leída" y "Marcar todas".
 * - El badge/panel mantienen la alerta hasta que el usuario la marque.
 */
const EmailNotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<ServiceEmailNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [toastQueue, setToastQueue] = useState<ServiceEmailNotification[]>([]);
  const toastedIdsRef = useRef(new Set<number>());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read_at).length,
    [notifications],
  );

  const enqueueFreshToasts = useCallback((items: ServiceEmailNotification[]) => {
    const fresh = items.filter(
      (notification) =>
        Number.isInteger(notification?.id) &&
        !notification.read_at &&
        !toastedIdsRef.current.has(notification.id),
    );
    if (!fresh.length) return;

    fresh.forEach((notification) => toastedIdsRef.current.add(notification.id));
    setToastQueue((current) => {
      const queuedIds = new Set(current.map((item) => item.id));
      const toAdd = fresh.filter((item) => !queuedIds.has(item.id));
      return toAdd.length ? [...current, ...toAdd] : current;
    });
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetchWithSession(EndPoints.SERVICES.EMAILS_NOTIFICATIONS, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });
      if (!res.ok) {
        return;
      }

      const body = await res.json().catch(() => null);
      const items = Array.isArray(body?.data?.notifications)
        ? (body.data.notifications as ServiceEmailNotification[])
        : [];
      setNotifications(items);
      enqueueFreshToasts(items);
    } catch {
      /* el poll reintenta en el siguiente ciclo */
    }
  }, [enqueueFreshToasts]);

  useEffect(() => {
    void fetchNotifications();
    const intervalId = window.setInterval(() => {
      void fetchNotifications();
    }, POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!isOpen) return;
    void fetchNotifications();
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, fetchNotifications]);

  const markAsRead = useCallback(async (notificationId: number) => {
    setNotifications((current) =>
      current.map((notification) =>
        notification.id === notificationId
          ? { ...notification, read_at: new Date().toISOString() }
          : notification,
      ),
    );
    setToastQueue((current) => current.filter((item) => item.id !== notificationId));

    try {
      await fetchWithSession(
        EndPoints.SERVICES.EMAILS_NOTIFICATIONS_READ.replace(':id', String(notificationId)),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
        },
      );
    } catch {
      /* el siguiente poll corrige el estado */
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setNotifications((current) =>
      current.map((notification) =>
        notification.read_at
          ? notification
          : { ...notification, read_at: new Date().toISOString() },
      ),
    );
    setToastQueue([]);

    try {
      await fetchWithSession(EndPoints.SERVICES.EMAILS_NOTIFICATIONS_READ_ALL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      });
    } catch {
      void fetchNotifications();
    }
  }, [fetchNotifications]);

  /** Cierra el toast sin marcar leída: el badge/panel siguen mostrando la alerta. */
  const dismissToast = useCallback(() => {
    setToastQueue((current) => current.slice(1));
  }, []);

  const openNotification = useCallback(
    (notification: ServiceEmailNotification) => {
      setIsOpen(false);
      setToastQueue((queue) => queue.filter((item) => item.id !== notification.id));
      if (notification.type === 'UPCOMING_SERVICE') {
        const serviceId = toServiceId(notification.payload?.service_id);
        navigate(serviceId ? `/services/edit/${serviceId}` : '/services');
        return;
      }
      const emailId = toServiceId(notification.payload?.email_id);
      navigate(emailId ? `/emails/edit/${emailId}` : '/emails');
    },
    [navigate],
  );

  const currentToast = toastQueue[0];

  return (
    <>
      <div className="email-notification-bell" ref={containerRef}>
        <button
          type="button"
          className="email-notification-bell__button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label={`Notificaciones${unreadCount ? ` (${unreadCount} sin leer)` : ''}`}
          aria-expanded={isOpen}
        >
          <Bell size={20} strokeWidth={2.2} aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="email-notification-bell__badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div className="email-notification-bell__panel" role="dialog" aria-label="Notificaciones">
            <div className="email-notification-bell__panel-header">
              <strong>Notificaciones</strong>
              <div className="email-notification-bell__panel-header-actions">
                {unreadCount > 0 && <span>{unreadCount} sin leer</span>}
                {unreadCount > 0 && (
                  <button type="button" onClick={() => void markAllAsRead()}>
                    Marcar todas
                  </button>
                )}
              </div>
            </div>

            {notifications.length === 0 ? (
              <p className="email-notification-bell__empty">
                No hay notificaciones pendientes.
              </p>
            ) : (
              <ul className="email-notification-bell__list">
                {notifications.map((notification) => {
                  const route = [
                    notification.payload?.origin_name,
                    notification.payload?.destination_name,
                  ]
                    .filter(Boolean)
                    .join(' → ');
                  return (
                    <li
                      key={notification.id}
                      className={`email-notification-bell__item${
                        notification.read_at ? '' : ' email-notification-bell__item--unread'
                      }`}
                    >
                      <div className="email-notification-bell__item-head">
                        <strong className="email-notification-bell__item-title">
                          {notification.title}
                        </strong>
                        <time className="email-notification-bell__item-time">
                          {formatCreatedAt(notification.created_at)}
                        </time>
                      </div>
                      <p className="email-notification-bell__item-message">
                        {notification.message}
                      </p>
                      {route && (
                        <p className="email-notification-bell__item-route">{route}</p>
                      )}
                      <div className="email-notification-bell__item-actions">
                        <button type="button" onClick={() => openNotification(notification)}>
                          {notification.type === 'UPCOMING_SERVICE'
                            ? 'Ver servicio'
                            : 'Ver correo'}
                        </button>
                        {!notification.read_at && (
                          <button type="button" onClick={() => void markAsRead(notification.id)}>
                            Marcar leída
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {currentToast && (
        <EmailNotificationToast
          notification={currentToast}
          onOpen={() => {
            const current = currentToast;
            setToastQueue((queue) => queue.slice(1));
            openNotification(current);
          }}
          onDismiss={dismissToast}
        />
      )}
    </>
  );
};

export default EmailNotificationBell;
